import type { Point } from './painted-renderer.ts';

interface RoutineFrame { at: Point; moving: boolean; activity: 'idle' | 'work' }
/** A living worker travels at a measured pace, works at each end, then returns.
 * Paths are already resolved against the area's navigation. The host holds elapsed
 * time on pause/reduced motion; routines never write simulation or saved state. */
export function civilianRoutine(path: readonly Point[], elapsed: number, id: string, worker: boolean): RoutineFrame {
  const first = path[0]!;
  if (path.length < 2) return { at: first, moving: false, activity: worker ? 'work' : 'idle' };
  const lengths = path.slice(1).map((at, i) => Math.hypot(at[0] - path[i]![0], at[1] - path[i]![1]));
  const distance = lengths.reduce((sum, length) => sum + length, 0);
  if (!distance) return { at: first, moving: false, activity: worker ? 'work' : 'idle' };
  const speed = worker ? 25 : 20, travel = distance / speed, wait = worker ? 6 : 3.5;
  let seed = 0;
  for (const letter of id) seed = (Math.imul(seed, 31) + letter.charCodeAt(0)) >>> 0;
  const phase = (elapsed / 1000 + seed % 1700 / 100) % (2 * (travel + wait));
  if (phase < wait) return { at: first, moving: false, activity: worker ? 'work' : 'idle' };
  if (phase >= wait + travel && phase < 2 * wait + travel) return { at: path.at(-1)!, moving: false, activity: worker ? 'work' : 'idle' };
  let remaining = phase < wait + travel ? (phase - wait) * speed : distance - (phase - 2 * wait - travel) * speed;
  for (const [i, length] of lengths.entries()) {
    if (remaining <= length && length) {
      const a = path[i]!, b = path[i + 1]!, u = remaining / length;
      return { at: [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u], moving: true, activity: 'idle' };
    }
    remaining -= length;
  }
  return { at: path.at(-1)!, moving: false, activity: 'idle' };
}
